import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCreateEmployee, useUpdateEmployee } from '../hooks/useEmployee';
import { Employee } from '../types';

interface EmployeeFormDialogProps {
  open: boolean;
  onClose: () => void;
  employee?: Employee | null;
}

const EmployeeFormDialog: React.FC<EmployeeFormDialogProps> = ({ open, onClose, employee }) => {
  const isEditMode = !!employee;

  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    salary: 0,
    hire_date: new Date().toISOString().split('T')[0],
    is_active: 1,
  });

  useEffect(() => {
    if (employee) {
      setFormData({
        first_name: employee.first_name,
        last_name: employee.last_name,
        email: employee.email || '',
        phone_number: employee.phone_number,
        salary: parseFloat(employee.salary.replace(/[^0-9.]/g, '')),
        hire_date: employee.hire_date
          ? new Date(employee.hire_date).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        is_active: employee.is_active,
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        salary: 0,
        hire_date: new Date().toISOString().split('T')[0],
        is_active: 1,
      });
    }
  }, [employee, open]);

  const handleChange = (field: string) => (e: any) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        const { salary: _salary, is_active: _is_active, ...updateData } = formData;
        await updateEmployee.mutateAsync({
          id: employee.id,
          data: updateData,
        });
      } else {
        // remove every empty string field before sending
        const dataToSend: any = {};
        Object.entries(formData).forEach(([key, value]) => {
          if (value.toString().trim() !== '') {
            dataToSend[key] = value;
          }
        });

        await createEmployee.mutateAsync(dataToSend);
      }
      handleClose();
    } catch (error) {
      console.error('Failed to save employee:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      salary: 0,
      hire_date: new Date().toISOString().split('T')[0],
      is_active: 1,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={handleChange('first_name')}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={handleChange('last_name')}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone_number}
                  onChange={handleChange('phone_number')}
                  required
                />
              </Stack>
              {!isEditMode && (
                <TextField
                  fullWidth
                  label="Salary (₵)"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={formData.salary}
                  onChange={handleChange('salary')}
                  required
                />
              )}
              <TextField
                fullWidth
                label="Hired Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.hire_date}
                onChange={handleChange('hire_date')}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createEmployee.isPending || updateEmployee.isPending}
            sx={{
              background: 'linear-gradient(45deg, #ff9800 30%, #ffa726 90%)',
              borderRadius: '12px',
            }}
          >
            {createEmployee.isPending || updateEmployee.isPending
              ? 'Saving...'
              : isEditMode
                ? 'Update'
                : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EmployeeFormDialog;
